"use client";

/**
 * UsernameStep Component
 *
 * First step in the setup flow. User enters their Instagram/TikTok username.
 * Features:
 * - Animated platform toggle (Instagram/TikTok)
 * - Reuses InstagramSearchField/TiktokSearchField with autocomplete
 * - Uses Outfame Button component for consistent styling
 * - Prefetches assets for LoadingOverlay (step 2) after page load
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import InstagramSearchField from "@/components/presentational/tailwind/InstagramSearchField";
import TiktokSearchField from "@/components/presentational/tailwind/TikTokSearchField";
import Button from "@/components/outfame/Button";
import PlatformToggle from "@/components/outfame/PlatformToggle";
import { normalizeSocialHandle } from "@/utils/social-handle";

/** Polished shield icon with subtle emerald gradient */
function ShieldGradientIcon(t0) {
  const $ = _c(4);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "h-4 w-4" : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <defs><linearGradient id="shield-emerald-gradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#34d399" /><stop offset="100%" stopColor="#059669" /></linearGradient></defs>;
    t3 = <path fillRule="evenodd" clipRule="evenodd" d="M9.661 2.237a.75.75 0 0 1 .678 0 12.013 12.013 0 0 0 5.322 1.24.75.75 0 0 1 .739.665c.1.813.149 1.643.149 2.487 0 4.543-2.475 8.485-6.136 10.596a.75.75 0 0 1-.826 0C5.925 15.114 3.45 11.172 3.45 6.629c0-.844.05-1.674.149-2.487a.75.75 0 0 1 .74-.665 12.013 12.013 0 0 0 5.322-1.24Zm3.593 5.517a.75.75 0 0 0-1.008-1.108l-3.09 2.81-1.092-.993a.75.75 0 1 0-1.008 1.108l1.6 1.455a.75.75 0 0 0 1.008 0l3.59-3.272Z" fill="url(#shield-emerald-gradient)" />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== className) {
    t4 = <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">{t2}{t3}</svg>;
    $[2] = className;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}
import type { Platform } from "../../types";

/**
 * Prefetch critical images for LoadingOverlay after page is idle
 * This ensures the logo is ready when transitioning to step 2
 */
function usePrefetchLoadingAssets() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
}
function _temp2() {
  const prefetchImages = _temp;
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(prefetchImages, {
      timeout: 2000
    });
    return () => window.cancelIdleCallback(id);
  } else {
    const timeout = setTimeout(prefetchImages, 1000);
    return () => clearTimeout(timeout);
  }
}
function _temp() {
  const logo = new window.Image();
  logo.src = "/img/outfame-logo-mark.svg";
}
interface UsernameStepProps {
  platform: Platform;
  username: string;
  isLoading: boolean;
  error: string | null;
  onPlatformChange: (platform: Platform) => void;
  onUsernameChange: (username: string) => void;
  onAvatarUrlChange: (avatarUrl: string | null) => void;
  onSubmit: () => void;
  onTrackUsername?: (username: string, platform: Platform) => void;
  /** Existing usernames for duplicate detection (for logged-in users adding new accounts) */
  existingUsernames?: Set<string>;
}
export default function UsernameStep(t0) {
  const $ = _c(46);
  const {
    platform,
    username,
    isLoading,
    error,
    onPlatformChange,
    onUsernameChange,
    onAvatarUrlChange,
    onSubmit,
    onTrackUsername,
    existingUsernames
  } = t0;
  const [localError, setLocalError] = useState(null);
  usePrefetchLoadingAssets();
  let t1;
  if ($[0] !== existingUsernames || $[1] !== onSubmit || $[2] !== onTrackUsername || $[3] !== onUsernameChange || $[4] !== platform || $[5] !== username) {
    t1 = () => {
      const result = normalizeSocialHandle(username, platform);
      if (!result.ok) {
        setLocalError(result.message);
        return;
      }
      const cleanUsername = result.handle;
      if (existingUsernames?.has(result.normalized)) {
        setLocalError(`You've already added @${cleanUsername} to your account`);
        return;
      }
      setLocalError(null);
      if (cleanUsername !== username) {
        onUsernameChange(cleanUsername);
      }
      if (onTrackUsername) {
        onTrackUsername(cleanUsername, platform);
      }
      onSubmit();
    };
    $[0] = existingUsernames;
    $[1] = onSubmit;
    $[2] = onTrackUsername;
    $[3] = onUsernameChange;
    $[4] = platform;
    $[5] = username;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const runSubmit = t1;
  let t2;
  if ($[7] !== runSubmit) {
    t2 = e => {
      e.preventDefault();
      runSubmit();
    };
    $[7] = runSubmit;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleSubmit = t2;
  let t3;
  if ($[9] !== onUsernameChange) {
    t3 = value => {
      const cleanValue = value.replace(/^@+/, "");
      onUsernameChange(cleanValue);
      setLocalError(null);
    };
    $[9] = onUsernameChange;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleUsernameChange = t3;
  let t4;
  if ($[11] !== onAvatarUrlChange) {
    t4 = result_0 => {
      if (result_0 && "username" in result_0) {
        onAvatarUrlChange(result_0.profile_pic_url || result_0.avatar_url || null);
      } else {
        if (result_0 && "unique_id" in result_0) {
          onAvatarUrlChange(result_0.avatar_url || null);
        }
      }
    };
    $[11] = onAvatarUrlChange;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleResultSelected = t4;
  const displayError = error || localError;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h1 className="text-[28px] font-bold leading-tight tracking-tight text-ink lg:text-[32px]">Let's grow your account</h1>;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = platform === "instagram" ? "Instagram" : "TikTok";
  let t7;
  if ($[14] !== t6) {
    t7 = <div className="mb-8 text-center lg:text-left">{t5}<p className="mt-3 text-base text-black/60">Enter your {t6} username and we'll analyze your account to create a personalized growth strategy.</p></div>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== onPlatformChange || $[17] !== platform) {
    t8 = <div className="mb-6"><PlatformToggle platform={platform} onPlatformChange={onPlatformChange} fullWidth={true} /></div>;
    $[16] = onPlatformChange;
    $[17] = platform;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== handleResultSelected || $[20] !== handleUsernameChange || $[21] !== isLoading || $[22] !== platform || $[23] !== runSubmit || $[24] !== username) {
    t9 = platform === "instagram" ? <InstagramSearchField onValueChange={handleUsernameChange} onResultSelected={handleResultSelected} onEnterKeyPressed={runSubmit} disabled={isLoading} autoFocus={true} defaultValue={username} /> : <TiktokSearchField onValueChange={handleUsernameChange} onResultSelected={handleResultSelected} onEnterKeyPressed={runSubmit} disabled={isLoading} autoFocus={true} defaultValue={username} />;
    $[19] = handleResultSelected;
    $[20] = handleUsernameChange;
    $[21] = isLoading;
    $[22] = platform;
    $[23] = runSubmit;
    $[24] = username;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== displayError) {
    t10 = displayError && <motion.p initial={{
      opacity: 0,
      y: -5
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mt-2 text-sm text-red-500">{displayError}</motion.p>;
    $[26] = displayError;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t9) {
    t11 = <div>{t9}{t10}</div>;
    $[28] = t10;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== isLoading || $[32] !== username) {
    t12 = isLoading || !username.trim();
    $[31] = isLoading;
    $[32] = username;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = isLoading ? "Analyzing account..." : "Start Growing";
  let t14;
  if ($[34] !== t12 || $[35] !== t13) {
    t14 = <Button type="submit" variant="primary" size="lg" disabled={t12} className="w-full">{t13}</Button>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== handleSubmit || $[38] !== t11 || $[39] !== t14) {
    t15 = <form onSubmit={handleSubmit} className="flex flex-col gap-6">{t11}{t14}</form>;
    $[37] = handleSubmit;
    $[38] = t11;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="mt-auto pt-8"><div className="flex items-center justify-center gap-2"><ShieldGradientIcon className="h-4 w-4" /><p className="text-xs text-black/50">No password required. Your account stays secure.</p></div></div>;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t15 || $[43] !== t7 || $[44] !== t8) {
    t17 = <div className="flex flex-1 flex-col pt-4 lg:pt-8">{t7}{t8}{t15}{t16}</div>;
    $[42] = t15;
    $[43] = t7;
    $[44] = t8;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  return t17;
}